import { Link } from "react-router-dom";
import { ClipboardCheck, Plus, Trash2, ArrowUp, ArrowDown, Hourglass, Download } from "lucide-react";
import { fmtDateTime } from "../lib/api";
import { useApi, Panel, Empty, btn, inputCls } from "./kit";
import { reportPdf } from "../pages/WalkthroughReport";

function Steps({ items }) {
  return <ol className="list-decimal space-y-1.5 pl-5 text-[15px]">{items.map((t, i) => <li key={i}>{t}</li>)}</ol>;
}

function NotYet({ n, testid }) {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" data-testid={testid}>
      <p className="font-semibold">Not yet · observed {n.observed_date || fmtDateTime(n.at)} by {n.typed_name || n.by_name}</p>
      {n.practise?.length > 0 && <><p className="mt-1">To practise:</p><ul className="list-disc pl-5">{n.practise.map((t, i) => <li key={i}>{t}</li>)}</ul></>}
      <p className="mt-1">Comments: {n.note}</p>
      {n.signoff_id && <button className="mt-2 inline-flex items-center gap-1 text-sm font-semibold underline" onClick={() => reportPdf(n.signoff_id, n.observed_date)} data-testid={`${testid}-pdf`}><Download className="h-4 w-4" /> Walkthrough report (PDF)</button>}
    </div>
  );
}

export function WalkthroughStep({ a, v }) {
  const items = v.signoff?.items || [];
  const wt = a.walkthrough;
  return (
    <div className="space-y-4 rounded-2xl border bg-white p-6" data-testid="walkthrough-step">
      <div className="flex items-center gap-3"><ClipboardCheck className="h-6 w-6 text-[color:var(--dcbc-red)]" /><h2 className="text-2xl font-semibold">Manager walkthrough</h2></div>
      {!wt && a.awaiting_signoff && a.last_not_yet && <NotYet n={a.last_not_yet} testid="walkthrough-not-yet" />}
      {wt ? <div className="space-y-2" data-testid="walkthrough-signed"><p className="font-semibold text-emerald-700">Passed · observed {wt.observed_date || fmtDateTime(wt.at)}, signed off by {wt.typed_name || wt.by_name}</p>
          {wt.signoff_id && <button className={btn.ghost} onClick={() => reportPdf(wt.signoff_id, wt.observed_date)} data-testid="walkthrough-report-pdf"><Download className="h-4 w-4" /> Walkthrough report (PDF)</button>}</div>
        : a.awaiting_signoff ? <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="walkthrough-waiting"><Hourglass className="mt-0.5 h-4 w-4 shrink-0" /> {a.last_not_yet ? "When you've practised, talk to your manager to schedule another walkthrough." : "You've finished your part. Talk to your manager to schedule your walkthrough. We've let them know you're ready."} They'll watch you on the job and fill in the report, and then your course is complete.</p>
        : <p className="text-sm text-muted-foreground" data-testid="walkthrough-locked">Finish the sections, quiz and acknowledgements first. Then you'll schedule a walkthrough where your manager watches you do these steps on the job:</p>}
      <Steps items={items} />
    </div>
  );
}

export function WalkthroughPanel() {
  const { data } = useApi("/walkthroughs/pending");
  if (!data?.length) return null;
  return (
    <Panel title="Walkthroughs to do" testid="home-walkthroughs">
      <ul className="divide-y">{data.map((w) => (
        <li key={w.assignment_id} className="flex flex-wrap items-center justify-between gap-3 py-3">
          <span><span className="font-medium">{w.user_name}</span><span className="block text-sm text-muted-foreground">{w.course_title} v{w.version} · ready since {fmtDateTime(w.since)}{w.last_not_yet && " · last result: not yet"}</span></span>
          <Link to={`/walkthroughs/${w.assignment_id}`} className={btn.dark} data-testid={`walkthrough-open-${w.assignment_id}`}><ClipboardCheck className="h-4 w-4" /> Open walkthrough report</Link>
        </li>))}</ul>
    </Panel>
  );
}

export function SignoffEditor({ d, setD }) {
  const so = d.signoff || { required: false, items: [] };
  const set = (patch) => setD({ ...d, signoff: { ...so, ...patch } });
  const items = so.items || [];
  const setItem = (i, t) => set({ items: items.map((x, k) => (k === i ? t : x)) });
  const move = (i, dir) => { const n = [...items]; [n[i], n[i + dir]] = [n[i + dir], n[i]]; set({ items: n }); };
  return (
    <Panel title="Manager sign-off" testid="editor-signoff-panel">
      <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" className="h-5 w-5" checked={!!so.required} onChange={(e) => set({ required: e.target.checked })} data-testid="editor-signoff-required" /> Require a supervised walkthrough before this course counts as complete</label>
      <p className="mt-1 text-xs text-muted-foreground">The person's manager (or anyone you've allowed to sign off walkthroughs in People) ticks each step as they watch it, then signs off. It's saved as a practical sign-off in the person's records.</p>
      {so.required && <div className="mt-4 space-y-2">
        {items.length === 0 && <Empty>Add the steps the manager should watch.</Empty>}
        {items.map((t, i) => (
          <div key={i} className="flex gap-2">
            <input className={inputCls} value={t} aria-label={`Step ${i + 1}`} onChange={(e) => setItem(i, e.target.value)} data-testid={`editor-signoff-item-${i}`} />
            <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)} className="p-2 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
            <button type="button" aria-label="Move down" disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-2 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
            <button type="button" aria-label="Remove step" onClick={() => set({ items: items.filter((_, k) => k !== i) })} className="p-2"><Trash2 className="h-4 w-4" /></button>
          </div>))}
        <button className={btn.ghost} onClick={() => set({ items: [...items, ""] })} data-testid="editor-signoff-add"><Plus className="h-4 w-4" /> Step</button>
      </div>}
    </Panel>
  );
}
